/** Interaction design tokens. NO VALUE HERE IS MIRRORED ANY MORE (D3, cured 2026-08-24). The brand primary binds from
 * `const/brand-palette.ts`, the two greys bind their rungs from `const/color-ramps.ts` (colour's L0, hoisted out of the
 * brand token tree in the same wave), and the disabled opacity binds `opacity.muted` from `const/interaction-data.ts`.
 * The `utils/` floor admits const/, so every one of those homes was always reachable -- the hand-mirrors existed only
 * because colour's L0 sat at L2 inside `brand/federa/tokens/colors.ts`, which this floor cannot import. The drift pins
 * that stood in for the missing SSOT went with the mirrors: a value-equality assertion between two readers of the same
 * const is a tautology that would stay green through the exact regression it was written to catch. What the sibling
 * suite pins now is the IMPORT RELATIONSHIP -- this module must reach the SSOTs by import and must restate no hex they
 * own -- plus the ONE mirror that survives, described below. The hexToRgba conversion is bound from const/hex-rgba.ts
 * for the same reason, so the one name has one body.
 *
 * EVERY MIRROR THAT SURVIVES HERE IS ONE A CONSUMER RENDERS, which is why neither the error red (`#DC2626`) nor the
 * medium hover overlay (black at alpha 0.05) is exported: an exhaustive two-way census -- every
 * `from '...interaction-tokens'` import in the tree against every occurrence of each symbol -- found the sibling suite
 * as their ONLY reader, while the real importers take `disabledTokens` and `focus` alone. A drift pin over a value no
 * surface renders does not protect a consumer -- it keeps a mirror alive and makes the hex look like several
 * decisions -- and re-homing such a hex on the const floor would add a leaf to hold a value nothing reads. Adding
 * either export means adding its consumer in the same change. */

import { hexToRgba } from '@federa/const/hex-rgba';
import { BRAND_PALETTE } from '@federa/const/brand-palette';
import { COLOR_RAMPS } from '@federa/const/color-ramps';
import { opacity } from '@federa/const/interaction-data';

// The ONE surviving hand-mirror: the brand-layer disabled token (brand/federa/tokens/interaction.ts) composed here
// rather than imported, because the brand layer sits ABOVE this leaf in the one-way import model and `cursor` /
// `pointerEvents` have no const home to bind through the way the opacity does. The sibling test pins those two members
// against the brand record so the two cannot drift silently.
const canonicalDisabled = {
	opacity: opacity.muted,
	cursor: 'not-allowed' as const,
	pointerEvents: 'none' as const,
} as const;

export const brandPrimary = BRAND_PALETTE.primary;
export const textDisabled = COLOR_RAMPS.neutral[400];

/** Brand primary at the ONE alpha any surface actually renders: the focus-ring shadow below. A new tier is one `hexToRgba(brandPrimary, <alpha>)` line at its first real consumer, not a speculative palette. */
const brandPrimaryRgbaStrong = hexToRgba(brandPrimary, 0.2);

export const disabledTokens = {
	...canonicalDisabled,
	background: COLOR_RAMPS.neutral[100],
	color: textDisabled,
} as const;

export const focus = {
	ring: {
		/** Standard focus ring for most elements */
		default: {
			width: '2px',
			style: 'solid',
			color: brandPrimary,
			offset: '2px',
			boxShadow: `0 0 0 2px ${brandPrimaryRgbaStrong}`,
		},
	},
} as const;
